import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { ScrollView, StyleSheet } from 'react-native';

import { FadeView, theme, useResponsive } from '@sg/garnish';
import { useProductMicroNutritionData } from '@sg/shared-fe/features/menu';
import { ProductDetails } from '@sg/shared-fe/features/product-details';

import { ProductDetailsCarousel } from '@order/features/product-details';
import { useTelemetry } from '@order/Telemetry';

import {
  AppFooter,
  type MenuContentDeliveryDetailsData,
  MenuContentHeader,
  type MenuContentOrderChannel,
  type MenuContentRestaurantData,
} from '../../../../components';
import { useProductDetailsCarouselItems, useProductDetailsTabs } from '../../hooks';
import { messages } from '../../ProductDetails.messages';
import { type BaseProduct, type ProductDetailsMappedContent } from '../../ProductDetails.types';
import { ProductNutritionData } from '../ProductNutritionData';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsReadySM = (props: Props) => {
  const {
    baseProduct,
    productCost,
    restaurantData,
    deliveryDetailsData,
    orderChannel,
    locationName,
    content,
    hasNoSelectedLocation,
    isUnavailableProduct,
    isAddingToBag,
    navigateBack,
    onAddToBag,
    navigateToLocationSearch,
  } = props;

  const hasIngredientHighlights = content.ingredientHighlights.length > 0;
  const hasNutritionInfo = content.shouldShowNutritionInfoSection;
  const canAddToBag = !isUnavailableProduct && !hasNoSelectedLocation;

  // ─────────────────────────────────────────────────────────────────────

  const { minWidth } = useResponsive();
  const { trackEvent } = useTelemetry();
  const { formatMessage } = useIntl();

  const {
    isNutritionTabActive,
    isIngredientHighlightsTabActive,
    nutritionNativeId,
    nutritionPanelNativeId,
    ingredientHighlightsNativeId,
    ingredientHighlightsPanelNativeId,
    setActiveTabToIngredientHighlights,
    setActiveTabToNutrition,
  } = useProductDetailsTabs({ hasIngredientHighlights });

  // ─── Derived Data ────────────────────────────────────────────────────

  const ingredientHighlightsTitle = content.ingredientHighlightsTitle ?? formatMessage(messages.ingredientHighlightsTitle); // prettier-ignore
  const nutritionInfoTitle = content.nutritionInfoTitle ?? formatMessage(messages.nutritionInfoTitle); // prettier-ignore

  const microNutritionEntries = useProductMicroNutritionData({ baseProduct });

  const { carouselItems } = useProductDetailsCarouselItems({
    baseProduct,
    carousel: content.carousel,
  });

  // ─── Flags ───────────────────────────────────────────────────────────

  const shouldRenderMacroNutritions = microNutritionEntries.length > 0 && !isNutritionTabActive;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onContactUs = useCallback(() => {
    trackEvent({ name: 'product-details.contact-us' });
  }, [trackEvent]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <FadeView show duration={theme.transitions.long} style={styles.outerContainer}>
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        {restaurantData ? (
          <MenuContentHeader
            restaurantData={restaurantData}
            deliveryDetailsData={deliveryDetailsData}
            locationName={locationName}
            orderChannel={orderChannel}
            compact
            changeLocation={navigateToLocationSearch}
            onContactUs={onContactUs}
          />
        ) : null}

        <ProductDetails.Container>
          <ProductDetails.Column withBorder centered>
            <ProductDetails.BackButton
              onPress={navigateBack}
              aria-label={formatMessage(messages.goBack)}
            />

            {carouselItems.length === 0 ? (
              <ProductDetails.Banner.Large
                productName={baseProduct.name}
                productImageUrl={baseProduct.asset.url}
              />
            ) : null}

            {carouselItems.length > 0 ? <ProductDetailsCarousel items={carouselItems} /> : null}
          </ProductDetails.Column>

          <ProductDetails.Column>
            <ScrollView
              style={styles.detailsContainer}
              contentContainerStyle={styles.detailsContentContainer}
              showsVerticalScrollIndicator={false}
            >
              <ProductDetails.ContentBlock withBorder>
                {isUnavailableProduct ? <ProductDetails.UnavailableProduct /> : null}

                <ProductDetails.Header.Large>
                  <ProductDetails.Title color={theme.colors.KALE}>
                    {baseProduct.name}
                  </ProductDetails.Title>

                  {canAddToBag && minWidth.isMD ? (
                    <ProductDetails.AddToBagButton onPress={onAddToBag} isLoading={isAddingToBag} />
                  ) : null}

                  {!canAddToBag && minWidth.isMD ? (
                    <ProductDetails.ChangeLocationButton
                      hasNoSelectedLocation={hasNoSelectedLocation}
                      onPress={navigateToLocationSearch}
                    />
                  ) : null}
                </ProductDetails.Header.Large>

                {content.subheading ? (
                  <ProductDetails.Text color={theme.colors.BLACK}>
                    {content.subheading}
                  </ProductDetails.Text>
                ) : null}

                <ProductDetails.Text color={theme.colors.BLACK}>
                  {content.description ?? baseProduct.description}
                </ProductDetails.Text>

                <ProductDetails.Text color={theme.colors.BLACK}>
                  {typeof productCost === 'number' ? (
                    <>
                      <ProductDetails.Cost costInCents={productCost} />

                      {shouldRenderMacroNutritions ? <ProductDetails.TextSeparator /> : null}
                    </>
                  ) : null}

                  {shouldRenderMacroNutritions ? (
                    <ProductDetails.NutritionData entries={microNutritionEntries} />
                  ) : null}
                </ProductDetails.Text>

                {!canAddToBag || minWidth.isMD ? null : (
                  <ProductDetails.AddToBagButton onPress={onAddToBag} isLoading={isAddingToBag} />
                )}

                {canAddToBag || minWidth.isMD ? null : (
                  <ProductDetails.ChangeLocationButton
                    hasNoSelectedLocation={hasNoSelectedLocation}
                    onPress={navigateToLocationSearch}
                  />
                )}
              </ProductDetails.ContentBlock>

              <ProductDetails.ContentBlock>
                <ProductDetails.Tabs.LinksNav>
                  {hasIngredientHighlights ? (
                    <ProductDetails.Tabs.Link
                      title={ingredientHighlightsTitle}
                      active={isIngredientHighlightsTabActive}
                      nativeID={ingredientHighlightsNativeId}
                      panelNativeID={ingredientHighlightsPanelNativeId}
                      onPress={setActiveTabToIngredientHighlights}
                    />
                  ) : null}

                  {hasNutritionInfo ? (
                    <ProductDetails.Tabs.Link
                      title={nutritionInfoTitle}
                      active={isNutritionTabActive}
                      nativeID={nutritionNativeId}
                      panelNativeID={nutritionPanelNativeId}
                      onPress={setActiveTabToNutrition}
                    />
                  ) : null}
                </ProductDetails.Tabs.LinksNav>

                {hasIngredientHighlights && isIngredientHighlightsTabActive ? (
                  <ProductDetails.Tabs.Tab
                    nativeID={ingredientHighlightsPanelNativeId}
                    tabLinkID={ingredientHighlightsNativeId}
                  >
                    <ProductDetails.IngredientsStack>
                      {content.ingredientHighlights.map((entry) => (
                        <ProductDetails.Ingredient.Container key={entry.name}>
                          <ProductDetails.Ingredient.Image
                            alt={entry.name}
                            baseUrl={entry.image.fields.file.url}
                          />

                          <ProductDetails.Ingredient.ContentContainer>
                            <ProductDetails.Ingredient.Title>
                              {entry.name}
                            </ProductDetails.Ingredient.Title>

                            <ProductDetails.Ingredient.Highlights highlights={entry.highlights} />
                          </ProductDetails.Ingredient.ContentContainer>
                        </ProductDetails.Ingredient.Container>
                      ))}
                    </ProductDetails.IngredientsStack>
                  </ProductDetails.Tabs.Tab>
                ) : null}

                {hasNutritionInfo && isNutritionTabActive ? (
                  <ProductDetails.Tabs.Tab
                    nativeID={nutritionPanelNativeId}
                    tabLinkID={nutritionNativeId}
                  >
                    <ProductNutritionData baseProduct={baseProduct} />
                  </ProductDetails.Tabs.Tab>
                ) : null}
              </ProductDetails.ContentBlock>
            </ScrollView>
          </ProductDetails.Column>
        </ProductDetails.Container>

        <AppFooter />
      </ScrollView>
    </FadeView>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  contentContainer: {
    flex: 1,
  },
  detailsContainer: {
    minHeight: '100%',
  },
  detailsContentContainer: {
    flexGrow: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  baseProduct: BaseProduct;
  productCost: number | undefined;
  restaurantData: MenuContentRestaurantData | null | undefined;
  deliveryDetailsData: MenuContentDeliveryDetailsData | undefined | null;
  orderChannel: MenuContentOrderChannel;
  locationName: string;
  content: ProductDetailsMappedContent;
  hasNoSelectedLocation: boolean;
  isUnavailableProduct: boolean;
  isAddingToBag: boolean;
  navigateBack: () => void;
  navigateToLocationSearch: () => void;
  onAddToBag: () => void;
};
